/**
 * LoginGate (H) — wraps the app behind auth. Checks the session on mount and
 * renders the Sign-in screen for anonymous visitors, otherwise the app. The
 * account menu (change-password + sign-out) lives at the foot of the sidebar via
 * <AccountMenu/>, not here, so it never overlays page nav.
 */

import { useEffect, useState, type ReactNode } from "react";
import { getMe, login, requestLoginLink, type Me } from "../auth";
import { skinFromLocation } from "../skins";

export function LoginGate({ children }: { children: (me: Me) => ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    let alive = true;
    getMe().then(m => alive && setMe(m)).catch(() => alive && setMe({ authenticated: false }));
    return () => { alive = false; };
  }, []);

  if (me === null) return <div style={S.center}><div style={S.muted}>Loading…</div></div>;
  if (!me.authenticated) return <LoginScreen onAuthed={() => getMe().then(setMe).catch(() => {})} />;
  return <>{children(me)}</>;
}

type Mode = "password" | "link";

/** A failed/expired email link redirects back with ?login_link=invalid. */
function readLinkError(): string {
  if (new URLSearchParams(location.search).get("login_link") === "invalid") {
    return "That sign-in link is invalid or has expired. Request a new one below.";
  }
  return "";
}

function LoginScreen({ onAuthed }: { onAuthed: () => void }) {
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(readLinkError);
  const [linkSent, setLinkSent] = useState(false);

  // Clear the ?login_link=invalid flag from the URL once we've shown it.
  useEffect(() => {
    if (new URLSearchParams(location.search).has("login_link")) {
      history.replaceState(null, "", location.pathname);
    }
  }, []);

  const switchMode = (m: Mode) => { setMode(m); setError(""); setLinkSent(false); };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email.trim(), password);
      onAuthed();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const submitLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await requestLoginLink(email.trim());
      setLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setBusy(false);
  };

  return (
    <div style={S.center}>
      <div style={S.card}>
        <h1 style={S.title}>{skinFromLocation()?.title ?? "Nexus"}</h1>

        {mode === "password" ? (
          <>
            <p style={S.muted}>Sign in to your account.</p>
            <form onSubmit={submitPassword}>
              <input style={S.input} type="email" placeholder="you@company.com" value={email}
                     onChange={e => setEmail(e.target.value)} autoComplete="username" autoFocus required />
              <input style={S.input} type="password" placeholder="Password" value={password}
                     onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
              <button style={S.button} type="submit" disabled={busy}>{busy ? "…" : "Sign in"}</button>
            </form>
            <button style={S.linkBtn} type="button" onClick={() => switchMode("link")}>
              Email me a sign-in link instead
            </button>
          </>
        ) : linkSent ? (
          <>
            <p style={S.muted}>
              If an account exists for <b>{email.trim()}</b>, a sign-in link is on its way.
              Open it on this device to continue. The link expires in 15 minutes.
            </p>
            <button style={S.linkBtn} type="button" onClick={() => switchMode("password")}>
              Back to password sign-in
            </button>
          </>
        ) : (
          <>
            <p style={S.muted}>We'll email you a one-time link to sign in — no password needed.</p>
            <form onSubmit={submitLink}>
              <input style={S.input} type="email" placeholder="you@company.com" value={email}
                     onChange={e => setEmail(e.target.value)} autoComplete="username" autoFocus required />
              <button style={S.button} type="submit" disabled={busy}>{busy ? "…" : "Send sign-in link"}</button>
            </form>
            <button style={S.linkBtn} type="button" onClick={() => switchMode("password")}>
              Sign in with a password instead
            </button>
          </>
        )}

        {error && <div style={S.err}>{error}</div>}
      </div>
    </div>
  );
}

// Theme-variable driven so the sign-in screen follows the app palette —
// including per-instance skins (the gate renders inside a /<instance> URL).
const S: Record<string, React.CSSProperties> = {
  center: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" },
  card: { width: 360, padding: 32, background: "var(--bg-elev)", borderRadius: 12, border: "1px solid var(--border)", color: "var(--fg)", fontFamily: "system-ui, sans-serif" },
  title: { margin: "0 0 8px", fontSize: 24, fontWeight: 700 },
  muted: { color: "var(--fg-dim)", fontSize: 14, lineHeight: 1.5, margin: "0 0 8px" },
  input: { width: "100%", boxSizing: "border-box", padding: "10px 12px", margin: "10px 0 0", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--fg)", fontSize: 14 },
  button: { width: "100%", padding: "10px 12px", marginTop: 14, background: "var(--accent)", color: "var(--accent-fg)", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  linkBtn: { width: "100%", padding: "8px 0 0", marginTop: 12, background: "none", color: "var(--fg-dim)", border: "none", fontSize: 13, cursor: "pointer", textAlign: "center", textDecoration: "underline" },
  err: { fontSize: 13, color: "var(--red)", marginTop: 10 },
};
